import { formatMoney, formatWeight, useApiErrorMessage, useTranslation } from "@roastery/i18n";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Label,
  PageHeader,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  toast,
} from "@roastery/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { BookmarkIcon, RepeatIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import { SaveTemplateDialog } from "../components/save-template-dialog";
import { PortalLegalLinks } from "../components/legal-links";
import { StandingOrderDialog } from "../components/standing-order-dialog";
import { usePlacePortalOrder, usePortalAddresses, usePortalIdentity } from "../lib/portal-api";
import { type CartLine, cartKeyOf, useCart } from "../lib/use-cart";
import { useIsWide } from "../lib/use-media-query";
import { CreditBlockNotice } from "../components/credit-block-notice";
import { usePortalFormatSettings } from "../lib/use-portal-format";
import { DateField } from "../components/date-field";

export const Route = createFileRoute("/_authed/cart")({
  component: CartPage,
  // The confirmation lives in the address, not in component state. Clicking "Cart" in the header
  // goes to /cart with no search and lands on an empty cart, which is what the buyer expects after
  // ordering; a flag in state would have kept the old receipt on screen instead.
  validateSearch: (search: Record<string, unknown>): { placed?: string } => ({
    placed: typeof search.placed === "string" && search.placed ? search.placed : undefined,
  }),
});

/**
 * The cart, and the one form that turns it into an order (US-10.3).
 *
 * <p>Everything the customer chooses here is a choice they are allowed to make: which of their own
 * addresses, when they would like it, and a sentence for the roastery. Prices are not among them —
 * the server prices every line again from the same lists that drew the shelf, so what is shown here
 * is a quote, not an instruction.
 */
function CartPage() {
  const { t } = useTranslation();
  const errorMessage = useApiErrorMessage();
  const navigate = useNavigate();
  const settings = usePortalFormatSettings();
  const identity = usePortalIdentity();
  const addresses = usePortalAddresses();
  const cart = useCart(cartKeyOf(identity.data?.portalUserId, identity.data?.customerId));
  const place = usePlacePortalOrder();
  const wide = useIsWide();

  const [shippingAddressId, setShippingAddressId] = useState("");
  const [requestedDeliveryDate, setRequestedDeliveryDate] = useState("");
  const [notes, setNotes] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [savingStanding, setSavingStanding] = useState(false);
  const { placed } = Route.useSearch();

  const shipping = (addresses.data ?? []).filter((address) => address.kind === "SHIPPING");
  const minimum = Number(identity.data?.roastery?.minOrderNet ?? 0);
  // Whether, not why (US-25.3): a café is a customer of this roastery, not a party to its ledger, so
  // it is told that an order will not go through and who to ring — not the arithmetic behind it.
  const creditBlock = identity.data?.creditBlock;
  const missing = Math.max(0, minimum - cart.totalNet);
  const belowMinimum = missing > 0;

  if (placed) {
    return (
      // The receipt is the screen's heading: the one sentence a buyer came back to read.
      <div className="flex flex-col gap-4" data-testid="portal-order-placed">
        <PageHeader
          title={t("portal.cart.placed.title", { orderNumber: placed })}
          description={t("portal.cart.placed.description")}
        />
        <div className="flex flex-wrap gap-2">
          <Button data-testid="portal-back-to-catalog" onClick={() => void navigate({ to: "/" })}>
            {t("portal.cart.placed.backToCatalog")}
          </Button>
          <Button
            variant="outline"
            data-testid="portal-go-to-orders"
            onClick={() => void navigate({ to: "/orders" })}
          >
            {t("portal.nav.orders")}
          </Button>
        </div>
      </div>
    );
  }

  if (cart.lines.length === 0) {
    return (
      <div className="flex flex-col gap-4" data-testid="portal-cart-empty">
        <PageHeader title={t("portal.cart.title")} />
        <Card>
          <CardHeader>
            <CardDescription>{t("portal.cart.empty")}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button data-testid="portal-back-to-catalog" onClick={() => void navigate({ to: "/" })}>
              {t("portal.cart.placed.backToCatalog")}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  const submit = () => {
    place.mutate(
      {
        body: {
          shippingAddressId: shippingAddressId || undefined,
          requestedDeliveryDate: requestedDeliveryDate || undefined,
          notes: notes.trim() || undefined,
          lines: cart.lines.map((line) => ({ variantId: line.variantId, quantity: line.quantity })),
        },
      },
      {
        onSuccess: (order) => {
          cart.clear();
          void navigate({ to: "/cart", search: { placed: order.orderNumber ?? "" } });
        },
        onError: (error) => toast.error(errorMessage(error)),
      },
    );
  };

  const quantityInput = (line: CartLine) => (
    <Input
      type="number"
      min={1}
      max={9999}
      value={line.quantity}
      aria-label={t("portal.cart.quantityFor", { product: line.productName, sku: line.sku })}
      data-testid={`portal-cart-quantity-${line.sku}`}
      className="w-20 tabular-nums"
      onChange={(event) =>
        cart.setQuantity(line.variantId, Math.max(1, Math.min(9999, Number(event.target.value) || 1)))
      }
    />
  );

  const removeButton = (line: CartLine) => (
    <Button
      variant="ghost"
      size="sm"
      aria-label={t("portal.cart.remove")}
      data-testid={`portal-cart-remove-${line.sku}`}
      onClick={() => cart.remove(line.variantId)}
    >
      <Trash2Icon aria-hidden />
    </Button>
  );

  return (
    <div className="flex flex-col gap-4" data-testid="portal-cart">
      <PageHeader title={t("portal.cart.title")} description={t("portal.cart.description")} />

      <Card>
        <CardContent className="pt-6">
          {wide ? (
            <Table data-testid="portal-cart-lines">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("portal.cart.columns.product")}</TableHead>
                  <TableHead>{t("portal.catalog.columns.size")}</TableHead>
                  <TableHead>{t("portal.catalog.columns.price")}</TableHead>
                  <TableHead>{t("portal.cart.columns.quantity")}</TableHead>
                  <TableHead className="text-right">{t("portal.cart.columns.lineTotal")}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {cart.lines.map((line) => (
                  <TableRow key={line.variantId} data-testid={`portal-cart-line-${line.sku}`}>
                    <TableCell>
                      <div className="font-medium">{line.productName}</div>
                      <div className="text-xs text-muted-foreground">{line.sku}</div>
                    </TableCell>
                    <TableCell>{formatWeight(line.packageSizeGrams, settings)}</TableCell>
                    <TableCell className="tabular-nums">{formatMoney(line.unitPriceNet, settings)}</TableCell>
                    <TableCell>{quantityInput(line)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {formatMoney(line.unitPriceNet * line.quantity, settings)}
                    </TableCell>
                    <TableCell className="text-right">{removeButton(line)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <ul className="flex flex-col gap-3" data-testid="portal-cart-lines">
              {cart.lines.map((line) => (
                <li
                  key={line.variantId}
                  className="flex flex-col gap-2 rounded-md border border-border p-3"
                  data-testid={`portal-cart-line-${line.sku}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium">{line.productName}</div>
                      <div className="text-sm text-muted-foreground">
                        {[
                          formatWeight(line.packageSizeGrams, settings),
                          line.grind ? t(`portal.grind.${line.grind}`) : null,
                          formatMoney(line.unitPriceNet, settings),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </div>
                    {removeButton(line)}
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    {quantityInput(line)}
                    <span className="tabular-nums font-medium">
                      {formatMoney(line.unitPriceNet * line.quantity, settings)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
        <CardFooter className="flex-col items-stretch gap-1 border-t border-border pt-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{t("portal.cart.totalNet")}</span>
            <span className="tabular-nums text-base font-semibold" data-testid="portal-cart-total">
              {formatMoney(cart.totalNet, settings)}
            </span>
          </div>
          {/* The portal knows no VAT rates (they are the roastery's, per country and product), so
              the gross is not guessed here — the confirmation and the invoice carry it. */}
          <p className="text-xs text-muted-foreground" data-testid="portal-cart-vat-note">
            {t("portal.cart.vatNote")}
          </p>
          {belowMinimum ? (
            <p className="text-sm text-destructive" data-testid="portal-cart-below-minimum">
              {t("portal.cart.belowMinimum", {
                minimum: formatMoney(minimum, settings),
                missing: formatMoney(missing, settings),
              })}
            </p>
          ) : null}
        </CardFooter>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("portal.cart.delivery.title")}</CardTitle>
          <CardDescription>{t("portal.cart.delivery.description")}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="portal-shipping-address">{t("portal.cart.delivery.address")}</Label>
            <Select
              id="portal-shipping-address"
              data-testid="portal-shipping-address"
              value={shippingAddressId}
              onChange={(event) => setShippingAddressId(event.target.value)}
            >
              <option value="">{t("portal.cart.delivery.defaultAddress")}</option>
              {shipping.map((address) => (
                <option key={address.id} value={address.id}>
                  {[address.label, address.line1, address.city].filter(Boolean).join(" · ")}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="portal-delivery-date">{t("portal.cart.delivery.date")}</Label>
            <DateField
              id="portal-delivery-date"
              data-testid="portal-delivery-date"
              value={requestedDeliveryDate}
              onChange={setRequestedDeliveryDate}
            />
            <p className="text-xs text-muted-foreground">{t("portal.cart.delivery.dateHint")}</p>
          </div>
          <div className="flex flex-col gap-1.5 md:col-span-2">
            <Label htmlFor="portal-notes">{t("portal.cart.delivery.notes")}</Label>
            <Textarea
              id="portal-notes"
              rows={3}
              data-testid="portal-notes"
              value={notes}
              maxLength={2000}
              onChange={(event) => setNotes(event.target.value)}
            />
          </div>
        </CardContent>
        {creditBlock ? (
          <CardContent>
            <CreditBlockNotice block={creditBlock} testId="portal-credit-block" />
          </CardContent>
        ) : null}
        {/* On a phone the buttons stack, full width, with the one that places the order on top —
            in a row they ran off the right edge and "Place order" was the one that went first. */}
        <CardFooter className="flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs text-muted-foreground" data-testid="portal-payment-terms">
            {t("portal.cart.paymentTerms", { count: identity.data?.paymentTermsDays ?? 0 })}
          </span>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
            <Button
              variant="outline"
              data-testid="portal-cart-save-template"
              onClick={() => setSavingTemplate(true)}
            >
              <BookmarkIcon aria-hidden />
              {t("portal.orders.detail.saveAsTemplate")}
            </Button>
            {/* A standing order placed while the account is blocked would only be skipped on its
                morning (US-25.3), so it is not offered to begin with. */}
            <Button
              variant="outline"
              data-testid="portal-cart-standing"
              disabled={Boolean(creditBlock)}
              onClick={() => setSavingStanding(true)}
            >
              <RepeatIcon aria-hidden />
              {t("portal.standing.fromCart")}
            </Button>
            <Button
              className="order-first sm:order-none"
              data-testid="portal-place-order"
              disabled={belowMinimum || place.isPending || Boolean(creditBlock)}
              onClick={submit}
            >
              {place.isPending ? t("common.saving") : t("portal.cart.place")}
            </Button>
          </div>
        </CardFooter>
        {/* Beside the button that hands over an address and an order, not three clicks away in a
            footer: this is the moment a buyer gives us something (US-37.9, L-11). */}
        <CardContent className="pt-0">
          <PortalLegalLinks variant="short" testId="portal-cart-legal" />
        </CardContent>
      </Card>

      <SaveTemplateDialog
        open={savingTemplate}
        onClose={() => setSavingTemplate(false)}
        lines={cart.lines.map((line) => ({ variantId: line.variantId, quantity: line.quantity }))}
      />

      {/* A standing order starts from a cart, because that is where the customer has just decided
          what they want every week — and where prices and availability are on screen. */}
      {savingStanding ? (
        <StandingOrderDialog
          open
          onClose={() => setSavingStanding(false)}
          lines={cart.lines.map((line) => ({ variantId: line.variantId, quantity: line.quantity }))}
        />
      ) : null}
    </div>
  );
}
